/**
 * ReminderControl - the "Remind Me" row in the agent Properties card
 * (plan plans/2026-08-08-reminders-and-tasks.md A14).
 *
 * Two states:
 *   - a pending reminder exists: show "Sat, Aug 8 - morning" with an
 *     x affordance that clears it (any agent can see / clear it - the
 *     slot is per-ticket, not per-agent);
 *   - none: a compact set-control - date input (min = today in
 *     Eastern time) + day-part select + Set. Day parts already past
 *     today in Eastern time are disabled for today's date; the
 *     server-side 422 is the race backstop and surfaces as a toast.
 *
 * The select is a bare <select> rather than ui/Select because the
 * design-system Select has no per-option ``disabled`` support and
 * ui/* is an Auware mirror we do not extend.
 */
import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";

import { Button } from "@/components/ui";
import {
  useClearTicketReminder,
  useSetTicketReminder,
  useTicketReminder,
} from "@/hooks/useTicketReminder";
import {
  DAY_PART_OPTIONS,
  easternToday,
  formatReminderLabel,
  isDayPartDisabled,
  nextWeekday,
} from "@/lib/taskDates";
import type { ReminderDayPart } from "@/types/api";

export interface ReminderControlProps {
  ticketNumber: number;
}

export function ReminderControl({
  ticketNumber,
}: ReminderControlProps): JSX.Element {
  const reminder = useTicketReminder(ticketNumber);
  const setReminder = useSetTicketReminder(ticketNumber);
  const clearReminder = useClearTicketReminder(ticketNumber);

  const today = easternToday();
  // Defaults to the next weekday (Greg, 2026-08-08): reminders are a
  // workday tool, so from a Saturday the field starts on Monday.
  const [date, setDate] = useState(() => nextWeekday());
  const [dayPart, setDayPart] = useState<ReminderDayPart>("morning");
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  function flash(message: string) {
    setToast(message);
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  }

  // When the chosen date makes the selected part unavailable (picking
  // today mid-afternoon while "Morning" is selected), snap to the
  // first part still open so the Set button never submits a slot the
  // server would 422.
  useEffect(() => {
    if (!date) return;
    if (isDayPartDisabled(dayPart, date)) {
      const firstOpen = DAY_PART_OPTIONS.find(
        (o) => !isDayPartDisabled(o.value, date),
      );
      if (firstOpen) setDayPart(firstOpen.value);
    }
  }, [date, dayPart]);

  function submit() {
    if (!date) return;
    setReminder.mutate(
      { remind_on: date, day_part: dayPart },
      {
        onSuccess: () => {
          setDate(nextWeekday());
          setDayPart("morning");
        },
        onError: (err) => {
          if (isAxiosError(err) && err.response?.status === 422) {
            const detail = (
              err.response.data as { detail?: unknown } | undefined
            )?.detail;
            flash(
              typeof detail === "string"
                ? detail
                : "That reminder slot has already passed.",
            );
          } else {
            flash("Could not set the reminder.");
          }
        },
      },
    );
  }

  const pending = reminder.data ?? null;

  return (
    <div data-testid="reminder-control">
      <span className="block text-sm font-medium text-neutral-700">
        Remind Me
      </span>
      {pending ? (
        <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm">
          <span className="flex items-center gap-2 text-neutral-800">
            <i
              className="fa-duotone fa-thin fa-bell text-primary-600"
              aria-hidden="true"
            />
            <span data-testid="reminder-pending-label">
              {formatReminderLabel(pending.remind_on, pending.day_part)}
            </span>
          </span>
          <button
            type="button"
            onClick={() =>
              clearReminder.mutate(undefined, {
                onError: () => flash("Could not clear the reminder."),
              })
            }
            className="rounded-md p-1 text-neutral-400 transition-colors hover:text-neutral-600"
            aria-label="Clear reminder"
            data-testid="reminder-clear"
          >
            <i
              className="fa-duotone fa-thin fa-xmark text-base"
              aria-hidden="true"
            />
          </button>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2">
          <input
            type="date"
            value={date}
            min={today}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Reminder date"
            className="block w-32 shrink-0 rounded-md border border-neutral-300 px-2 py-1.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            data-testid="reminder-date-input"
          />
          <select
            value={dayPart}
            onChange={(e) => setDayPart(e.target.value as ReminderDayPart)}
            aria-label="Reminder time of day"
            // pr-8 reserves room for the browser chevron (Greg
            // 2026-08-08: it overlapped the label); the date input
            // gave up its flex-1 so this can take the leftover width.
            className="block min-w-0 flex-1 rounded-md border border-neutral-300 py-1.5 pl-2 pr-8 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            data-testid="reminder-day-part-select"
          >
            {DAY_PART_OPTIONS.map((o) => (
              <option
                key={o.value}
                value={o.value}
                disabled={!!date && isDayPartDisabled(o.value, date)}
              >
                {o.label}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!date}
            isLoading={setReminder.isPending}
            onClick={submit}
            data-testid="reminder-set"
          >
            Set
          </Button>
        </div>
      )}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-6 left-1/2 z-[10000] -translate-x-1/2"
        >
          <div className="rounded-lg bg-neutral-900 px-4 py-3 text-sm text-white shadow-lg ring-1 ring-black/10">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReminderControl;
